import { ConcurrentTaskType, UITask } from './types';
import { isTaskInProgress, isTaskQueued } from '@/utils/taskUtils';
import { AnyMeta, getHouseIdFromMeta } from './helpers';

/** Generates a batch id with time and random suffix */
export function generateBatchId(now: number = Date.now(), rnd: () => number = Math.random): string {
	return `batch_${now}_${rnd().toString(36).slice(2, 9)}`;
}

/** Checks if task metadata belongs to the given house id */
export function doesTaskBelongToHouse(meta: AnyMeta, houseId: number): boolean {
	const id = getHouseIdFromMeta(meta);
	return typeof id === 'number' && id === houseId;
}

/** Pure selection of queued tasks per type and house, excluding already dispatched */
export function selectQueuedTasksForType(params: {
	uiTasks: UITask[];
	taskType: ConcurrentTaskType;
	houseId: number;
	dispatched: Set<string>;
}): UITask[] {
	const { uiTasks, taskType, houseId, dispatched } = params;
	return uiTasks.filter(
		(t) =>
			t.taskType === taskType &&
			isTaskQueued(t.execution.apiTask) &&
			doesTaskBelongToHouse(t.execution.apiTask.metadata as AnyMeta, houseId) &&
			!dispatched.has(t.id),
	);
}

/** Computes concurrency limits using provided counts */
export function computeConcurrencyLimits(params: {
	taskType: ConcurrentTaskType;
	maxConcurrent: number;
	serverSideActive: number;
	uiTasks: UITask[];
	dispatchedPending: number;
}) {
	const { maxConcurrent, serverSideActive, uiTasks, taskType, dispatchedPending } = params;
	const clientSideActive = uiTasks.filter(
		(t) => t.taskType === taskType && isTaskInProgress(t.execution.apiTask),
	).length;
	const totalLoad = serverSideActive + clientSideActive + dispatchedPending;
	const availableSlots = Math.max(0, maxConcurrent - totalLoad);
	return {
		maxConcurrent,
		serverSideActive,
		clientSideActive,
		totalLoad,
		availableSlots,
		canAcceptMore: totalLoad < maxConcurrent,
	};
}

export function computeRetryDelayMs(retryCount: number, retryAfterSeconds?: number): number {
	// A 429 tells us exactly when a token frees up; honor it over the guess.
	if (retryAfterSeconds !== undefined && Number.isFinite(retryAfterSeconds) && retryAfterSeconds >= 0) {
		return retryAfterSeconds * 1000;
	}
	// retryCount before increment determines current delay step
	return Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s...
}
